import { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';

export default function ActiveSessionsManagement({ theme, currentUser }) {
  const { t } = useLanguage();
  const [activeSessions, setActiveSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [systemSettings, setSystemSettings] = useState(null);

  const fetchSystemSettings = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/system-settings`);
      if (response.ok) {
        const data = await response.json();
        setSystemSettings(data);
      }
    } catch (err) {
      console.error('Error fetching system settings:', err);
    }
  }, []);

  const fetchActiveSessions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/active-sessions`);
      if (!response.ok) {
        throw new Error('Failed to fetch active sessions');
      }
      const data = await response.json();
      setActiveSessions(data);
    } catch (err) {
      console.error('Error fetching active sessions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSystemSettings();
    fetchActiveSessions();
    // Refresh every 30 seconds
    const interval = setInterval(fetchActiveSessions, 30000);
    return () => clearInterval(interval);
  }, [fetchSystemSettings, fetchActiveSessions]);

  const handleForceLogout = async (userId, userName) => {
    if (!window.confirm(`${t('confirmForceLogout')}\n\n${t('userName')}: ${userName}`)) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/force-logout/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminUserId: currentUser?.id })
      });

      if (!response.ok) {
        throw new Error('Failed to force logout');
      }

      const result = await response.json();
      alert(`${t('success')}: ${result.message}`);

      // Refresh the sessions list
      fetchActiveSessions();
    } catch (err) {
      console.error('Error forcing logout:', err);
      alert(`${t('error')}: ${err.message}`);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatTimeSince = (dateString) => {
    if (!dateString) return '-';
    const now = new Date();
    const activityTime = new Date(dateString);
    const diffMs = now - activityTime;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return t('justNow');
    if (diffMins < 60) return `${diffMins} ${t('minutesAgo')}`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} ${t('hoursAgo')}`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} ${t('daysAgo')}`;
  };

  if (loading && activeSessions.length === 0) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        color: theme?.colors?.textSecondary || '#666'
      }}>
        {t('loading')}...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        color: theme?.colors?.error || '#f44336'
      }}>
        {t('error')}: {error}
      </div>
    );
  }

  return (
    <div style={{
      padding: '1.5rem',
      background: theme?.colors?.background || '#f5f5f5'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem'
      }}>
        <h3 style={{
          margin: 0,
          color: theme?.colors?.text || '#000'
        }}>
          {t('activeSessions')} ({activeSessions.length})
        </h3>
        <button
          onClick={fetchActiveSessions}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: theme?.colors?.secondary || '#666',
            color: theme?.colors?.textLight || '#fff',
            border: 'none',
            borderRadius: theme?.borderRadius || 4,
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}
        >
          🔄 {t('refresh')}
        </button>
      </div>

      {activeSessions.length === 0 ? (
        <div style={{
          padding: '2rem',
          textAlign: 'center',
          color: theme?.colors?.textSecondary || '#666',
          backgroundColor: theme?.colors?.surface || '#fff',
          borderRadius: theme?.borderRadius || 8
        }}>
          {t('noActiveSessions')}
        </div>
      ) : (
        <div style={{
          overflowX: 'auto'
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: theme?.colors?.surface || '#fff',
            borderRadius: theme?.borderRadius || 8,
            overflow: 'hidden'
          }}>
            <thead>
              <tr style={{
                backgroundColor: theme?.colors?.primary || '#1976d2',
                color: theme?.colors?.textLight || '#fff'
              }}>
                <th style={thStyle}>{t('userName')}</th>
                <th style={thStyle}>{t('fullName')}</th>
                <th style={thStyle}>{t('loginTime')}</th>
                <th style={thStyle}>{t('lastActivity')}</th>
                <th style={thStyle}>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {activeSessions.map((session, index) => (
                <tr
                  key={session.session_id}
                  style={{
                    backgroundColor: index % 2 === 0
                      ? (theme?.colors?.surface || '#fff')
                      : (theme?.colors?.background || '#f5f5f5'),
                    borderBottom: `1px solid ${theme?.colors?.border || '#ddd'}`
                  }}
                >
                  <td style={tdStyle}>{session.user_name || '-'}</td>
                  <td style={tdStyle}>
                    {session.first_names || session.last_names
                      ? `${session.first_names || ''} ${session.last_names || ''}`.trim()
                      : '-'}
                  </td>
                  <td style={tdStyle}>{formatDateTime(session.login_time)}</td>
                  <td style={tdStyle}>
                    {formatDateTime(session.last_activity)}
                    <br />
                    <small style={{ color: theme?.colors?.textSecondary || '#666' }}>
                      ({formatTimeSince(session.last_activity)})
                    </small>
                  </td>
                  <td style={tdStyle}>
                    {session.user_id !== currentUser?.id ? (
                      <button
                        onClick={() => handleForceLogout(session.user_id, session.user_name)}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: theme?.colors?.error || '#f44336',
                          color: theme?.colors?.textLight || '#fff',
                          border: 'none',
                          borderRadius: theme?.borderRadius || 4,
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          fontWeight: 'bold'
                        }}
                      >
                        {t('forceLogout')}
                      </button>
                    ) : (
                      <span style={{
                        color: theme?.colors?.textSecondary || '#666',
                        fontSize: '0.85rem'
                      }}>
                        ({t('currentUser')})
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{
        marginTop: '1rem',
        padding: '1rem',
        backgroundColor: theme?.colors?.surface || '#fff',
        borderRadius: theme?.borderRadius || 8,
        fontSize: '0.85rem',
        color: theme?.colors?.textSecondary || '#666'
      }}>
        <strong>ℹ️ {t('note')}:</strong>
        <ul style={{ marginTop: '0.5rem', marginBottom: 0, paddingLeft: '1.5rem' }}>
          <li>{t('sessionsRefreshAutomatically')}</li>
          <li>
            {systemSettings?.dailyLogoutEnabled !== false ? (
              <>
                {t('dailyAutoLogoutAt')} {' '}
                {String(systemSettings?.dailyLogoutHour || 5).padStart(2, '0')}:
                {String(systemSettings?.dailyLogoutMinute || 0).padStart(2, '0')} {' '}
                ({systemSettings?.dailyLogoutTimezone || 'America/Mexico_City'}). {' '}
                {t('changeInSystemSettings')}.
              </>
            ) : (
              <>
                {t('dailyAutoLogoutDisabled')}. {t('changeInSystemSettings')}.
              </>
            )}
          </li>
          <li>{t('cannotForceLogoutSelf')}</li>
        </ul>
      </div>
    </div>
  );
}

const thStyle = {
  padding: '1rem',
  textAlign: 'left',
  fontWeight: 'bold',
  fontSize: '0.9rem'
};

const tdStyle = {
  padding: '1rem',
  fontSize: '0.9rem'
};
